// biome-ignore-all lint/suspicious/noExplicitAny: not changing all these now
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  CustomFieldDto,
  GetAllParamsDto,
  GetPaginatedPriorityFrameworksResponseDto,
  GetPriorityFrameworkResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const usePriorityFrameworkApi = (httpClient: AxiosInstance) => {
  const baseUrl = '/priority-frameworks';
  const defaultLimit = 25;

  const getFrameworks = async (
    params?: GetAllParamsDto & { includeDisabledFrameworks?: boolean },
  ): Promise<GetPaginatedPriorityFrameworksResponseDto> => {
    const { data } = await httpClient.get<GetPaginatedPriorityFrameworksResponseDto>(`${baseUrl}`, {
      params: { ...params },
    });
    return data;
  };

  const getFramework = async (id: number): Promise<GetPriorityFrameworkResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}`).catch((error) => {
      if (error.response?.status === 404) {
        return { data: null };
      }
      throw error;
    });
    return data;
  };

  const useGetAll = (
    params?: GetAllParamsDto & { includeDisabledFrameworks?: boolean },
    enabled?: boolean,
  ) => {
    const limit = params?.limit ?? defaultLimit;
    return useInfiniteQuery({
      initialPageParam: 0,
      enabled: enabled ?? true,
      gcTime: 0,
      queryKey: [baseUrl, 'GetAll', params?.lifecycleFilter, params],
      queryFn: ({ pageParam }) => {
        const newParams = { ...params, limit, offset: pageParam * limit };
        return getFrameworks(newParams);
      },
      getNextPageParam: (lastPage) => {
        return lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined;
      },
    });
  };

  const useGetOne = (id: number, disable?: boolean) =>
    useQuery({
      queryKey: [baseUrl, id],
      queryFn: () => getFramework(id),
      enabled: !disable,
    });

  const getCustomFields = async (
    frameworkId?: number,
    createIfMissing?: boolean,
  ): Promise<{ data: CustomFieldDto[] }> => {
    const { data } = await httpClient.get<{ data: CustomFieldDto[] }>(
      `${baseUrl}/${frameworkId}/custom-fields`,
      {
        params: { createIfMissing },
      },
    );
    return data;
  };

  const useGetCustomFields = (frameworkId?: number, disable?: boolean) =>
    useQuery({
      queryKey: [baseUrl, frameworkId, 'custom-fields'],
      queryFn: () => getCustomFields(frameworkId),
      enabled: !disable && !!frameworkId,
    });

  const cloneCustomFields = async (
    frameworkId: number,
    fieldNameMapping: Record<string, string>,
  ): Promise<{ data: CustomFieldDto[] }> => {
    const { data } = await httpClient.post<{ data: CustomFieldDto[] }>(
      `${baseUrl}/${frameworkId}/clone-custom-fields`,
      { fieldNameMapping },
    );
    return data;
  };
  const useCloneCustomFields = () =>
    useErrorHandlingMutation({
      mutationFn: ({
        frameworkId,
        fieldNameMapping,
      }: {
        frameworkId: number;
        fieldNameMapping: Record<string, string>;
      }) => cloneCustomFields(frameworkId, fieldNameMapping),
    });

  return {
    useGetOne,
    useGetAll,
    useGetCustomFields,
    useCloneCustomFields,
  };
};
